/**
 * Capability Auto-Mapper (Rule-based v1.1)
 *
 * Infers training capability mappings from artifact title + description
 * using regex keyword rules. Used by CSV import to auto-create
 * training_artifact_capability_map rows, and by Mode 4 ticket recommendations.
 *
 * Rules support an optional `gate` (AND-gate): the rule only fires when both
 * the main `pattern` AND the `gate` match the text.
 */

import type { CapabilityKey } from '@/constants/trainingCapabilities';
import { clampWeight, validateCapabilityKey } from '@/constants/trainingCapabilities';

interface CapabilityRule {
  ruleId: string;
  pattern: RegExp;
  capability_key: CapabilityKey;
  base_weight: number;
  /** AND-gate: rule only fires if this secondary pattern also matches the text */
  gate?: RegExp;
}

export interface InferredCapability {
  capability_key: CapabilityKey;
  weight: number;
  is_primary: boolean;
}

// ---- Debug types (exported for dev tooling) ----

export interface DebugCapabilityHit {
  ruleId: string;
  contribution: number;
}

export interface InferredCapabilityDebug extends InferredCapability {
  /** Raw accumulated weight before clamping */
  rawScore: number;
  /** Which rules contributed, and how much each added */
  hits: DebugCapabilityHit[];
}

// =============================================
// RULES
// =============================================

/**
 * Keyword rules mapping regex patterns to capability keys with base weights.
 * If multiple rules hit the same capability, weights are summed (capped at 3.0).
 *
 * Tuning notes (v1.1):
 * - Generic triggers (diag_strategy, ADAS calibration) are AND-gated to reduce false positives
 * - High-signal technical terms (scope, CAN, voltage drop) boosted to 2.0+
 * - HVAC split into generic (1.3) and specific (1.6) to reduce noise
 * - Min threshold raised from 0.8 → 0.9 (with bypass for strong-match rules ≥ 2.0)
 */
const RULES: CapabilityRule[] = [

  // ── Testing & Measurement ──────────────────────────────────────────────

  {
    ruleId: 'elec_fund_1',
    pattern: /circuit|voltage|current|resistance|ohm/i,
    capability_key: 'test_electrical_fundamentals',
    base_weight: 2.0,
  },
  {
    // High-specificity electrical terms — very strong signal
    ruleId: 'elec_fund_2',
    pattern: /voltage\s*drop|load\s*test|ground\s*side|power\s*side|short\s*to\s*ground|short\s*to\s*power/i,
    capability_key: 'test_electrical_fundamentals',
    base_weight: 2.2,
  },
  {
    ruleId: 'oscilloscope_1',
    pattern: /scope|waveform|oscilloscope/i,
    capability_key: 'test_oscilloscope_usage',
    base_weight: 2.0, // boosted from 1.7 — unambiguous
  },
  {
    ruleId: 'scan_tool_1',
    pattern: /scan.?tool|pid|bi-?directional/i,
    capability_key: 'test_scan_tool_mastery',
    base_weight: 1.2,
  },
  {
    ruleId: 'network_diag_1',
    pattern: /can[\s.-]*bus|network[\s.-]*bus|can[\s.-]*network|data\s*bus/i,
    capability_key: 'test_network_diagnostics',
    base_weight: 2.0, // boosted from 1.7 — CAN bus is highly specific
  },
  {
    ruleId: 'nvh_1',
    pattern: /nvh|noise|vibration|harshness/i,
    capability_key: 'test_nvh_and_noise',
    base_weight: 1.5,
  },

  // ── Diagnostic Thinking ────────────────────────────────────────────────

  {
    // AND-gated: "strategy/approach" alone is too generic — requires diagnostic context
    ruleId: 'diag_strategy_1',
    pattern: /strategy|systematic|approach|diagnostic\s+process/i,
    capability_key: 'diag_strategy',
    base_weight: 1.1, // lowered from 1.5
    gate: /diagnos|fault|dtc|no.?start|misfire|network|circuit|waveform|scope|pid|can/i,
  },
  {
    ruleId: 'diag_pattern_1',
    pattern: /pattern|real[\s-]*world|case\s+study/i,
    capability_key: 'diag_pattern_recognition',
    base_weight: 1.1,
  },
  {
    ruleId: 'diag_info_first_1',
    pattern: /information[\s-]*first|info[\s-]*first/i,
    capability_key: 'diag_information_first',
    base_weight: 1.5,
  },
  {
    ruleId: 'diag_case_doc_1',
    pattern: /documentation|write[\s-]*up|repair\s+order/i,
    capability_key: 'diag_case_documentation',
    base_weight: 1.0,
  },
  {
    ruleId: 'diag_comeback_1',
    pattern: /comeback|come[\s-]*back/i,
    capability_key: 'diag_comeback_prevention',
    base_weight: 1.0,
  },

  // ── Systems Skill ──────────────────────────────────────────────────────

  {
    ruleId: 'engine_perf_1',
    pattern: /engine\s*(performance|manage|control)|drivability|driveability/i,
    capability_key: 'systems_engine_performance',
    base_weight: 1.5,
  },
  {
    // Direct ADAS terms — no gate needed
    ruleId: 'adas_direct_1',
    pattern: /adas|camera|radar|lidar/i,
    capability_key: 'systems_adas_calibration',
    base_weight: 1.8,
  },
  {
    // "calibrat" alone fires on throttle body, idle relearn, etc. — AND-gate required
    ruleId: 'adas_calibrat_1',
    pattern: /calibrat/i,
    capability_key: 'systems_adas_calibration',
    base_weight: 1.6,
    gate: /adas|camera|radar|lidar|lane|acc|blind.?spot/i,
  },
  {
    ruleId: 'hybrid_ev_1',
    pattern: /hybrid|ev[\s.-]*safety|high[\s.-]*voltage|electric\s+vehicle/i,
    capability_key: 'systems_hybrid_ev_safety',
    base_weight: 1.8,
  },
  {
    // HV-specific terms — very high confidence
    ruleId: 'hybrid_ev_2',
    pattern: /insulation|megohm|isolation|orange.?cable|hv.?safety|arc.?flash/i,
    capability_key: 'systems_hybrid_ev_safety',
    base_weight: 2.1,
  },
  {
    // Generic HVAC — lower weight to reduce noise
    ruleId: 'hvac_generic_1',
    pattern: /hvac|a\/c|climate.?control/i,
    capability_key: 'systems_hvac_diagnostics',
    base_weight: 1.3, // lowered from 1.5
  },
  {
    // HVAC-specific terms — high confidence
    ruleId: 'hvac_specific_1',
    pattern: /refrigerant|compressor|evaporator|condenser|vent.?temp/i,
    capability_key: 'systems_hvac_diagnostics',
    base_weight: 1.6,
  },
  {
    ruleId: 'transmission_1',
    pattern: /transmission|transaxle/i,
    capability_key: 'systems_transmission_driveability',
    base_weight: 1.5,
  },

  // ── Workflow & Efficiency ──────────────────────────────────────────────

  {
    ruleId: 'workflow_time_1',
    pattern: /time[\s.-]*manag|efficiency|productive/i,
    capability_key: 'workflow_time_management',
    base_weight: 0.8,
  },
  {
    ruleId: 'workflow_comm_1',
    pattern: /communicat|shop[\s.-]*flow/i,
    capability_key: 'workflow_shop_communication',
    base_weight: 0.8,
  },
  {
    ruleId: 'workflow_tool_1',
    pattern: /tooling|tool\s+setup/i,
    capability_key: 'workflow_tooling_and_setup',
    base_weight: 0.8,
  },

  // ── Advisor — Communication & Selling ─────────────────────────────────

  {
    ruleId: 'adv_story_1',
    pattern: /storytelling|inspection[\s.-]*sell|selling/i,
    capability_key: 'advisor_storytelling',
    base_weight: 1.5,
  },
  {
    ruleId: 'adv_objection_1',
    pattern: /objection/i,
    capability_key: 'advisor_objection_handling',
    base_weight: 1.5,
  },
  {
    ruleId: 'adv_expectation_1',
    pattern: /expectation/i,
    capability_key: 'advisor_expectation_setting',
    base_weight: 1.2,
  },
  {
    ruleId: 'adv_deescalat_1',
    pattern: /de[\s.-]*escalat/i,
    capability_key: 'advisor_deescalation',
    base_weight: 1.5,
  },
  {
    ruleId: 'adv_followup_1',
    pattern: /follow[\s.-]*up/i,
    capability_key: 'advisor_follow_up_systems',
    base_weight: 1.0,
  },

  // ── Advisor — Quality Workflow ─────────────────────────────────────────

  {
    ruleId: 'adv_dvi_1',
    pattern: /dvi|digital[\s.-]*inspect/i,
    capability_key: 'advisor_dvi_quality',
    base_weight: 1.5,
  },
  {
    ruleId: 'adv_repair_plan_1',
    pattern: /repair[\s.-]*plan|estimate\s+build/i,
    capability_key: 'advisor_repair_plan_building',
    base_weight: 1.3,
  },
  {
    ruleId: 'adv_comeback_intake_1',
    pattern: /comeback[\s.-]*intake/i,
    capability_key: 'advisor_comeback_intake',
    base_weight: 1.0,
  },

  // ── Manager/Owner — Shop Ops ───────────────────────────────────────────

  {
    ruleId: 'ops_capacity_1',
    pattern: /capacity[\s.-]*plan/i,
    capability_key: 'ops_capacity_planning',
    base_weight: 1.5,
  },
  {
    ruleId: 'ops_constraint_1',
    pattern: /constraint|bottleneck/i,
    capability_key: 'ops_constraint_management',
    base_weight: 1.3,
  },
  {
    ruleId: 'ops_process_1',
    pattern: /process[\s.-]*design|workflow[\s.-]*design/i,
    capability_key: 'ops_process_design',
    base_weight: 1.3,
  },
  {
    ruleId: 'ops_kpi_1',
    pattern: /kpi|metric|scorecard/i,
    capability_key: 'ops_kpi_literacy',
    base_weight: 1.2,
  },
  {
    ruleId: 'ops_quality_culture_1',
    pattern: /quality[\s.-]*culture/i,
    capability_key: 'ops_quality_culture',
    base_weight: 1.0,
  },

  // ── Manager/Owner — Leadership ─────────────────────────────────────────

  {
    ruleId: 'leadership_coaching_1',
    pattern: /coaching|mentoring/i,
    capability_key: 'leadership_coaching',
    base_weight: 1.3,
  },
  {
    ruleId: 'leadership_fairness_1',
    pattern: /fairness/i,
    capability_key: 'leadership_fairness_systems',
    base_weight: 1.0,
  },
  {
    ruleId: 'leadership_change_1',
    pattern: /change[\s.-]*manage/i,
    capability_key: 'leadership_change_management',
    base_weight: 1.3,
  },

  // ── Manager/Owner — Business ───────────────────────────────────────────

  {
    ruleId: 'biz_pricing_1',
    pattern: /pricing|labor[\s.-]*rate/i,
    capability_key: 'business_pricing_labor_strategy',
    base_weight: 1.3,
  },
  {
    ruleId: 'biz_marketing_1',
    pattern: /marketing|retention[\s.-]*rate/i,
    capability_key: 'business_marketing_retention',
    base_weight: 1.0,
  },
  {
    ruleId: 'biz_hiring_1',
    pattern: /hiring|onboarding/i,
    capability_key: 'business_hiring_onboarding',
    base_weight: 1.0,
  },
];

// =============================================
// CORE ENGINE (shared by both public functions)
// =============================================

const MIN_SCORE = 0.9;
const STRONG_MATCH_THRESHOLD = 2.0;
const PRIMARY_WEIGHT_THRESHOLD = 1.6;

interface AccumulatedEntry {
  rawScore: number;
  isStrongMatch: boolean;
  hits: DebugCapabilityHit[];
}

function runRules(text: string): Map<CapabilityKey, AccumulatedEntry> {
  const accumulated = new Map<CapabilityKey, AccumulatedEntry>();

  for (const rule of RULES) {
    // Main pattern check
    if (!rule.pattern.test(text)) continue;
    // AND-gate check (if present) — rule is skipped if gate fails
    if (rule.gate && !rule.gate.test(text)) continue;

    const existing = accumulated.get(rule.capability_key) ?? {
      rawScore: 0,
      isStrongMatch: false,
      hits: [],
    };

    existing.rawScore += rule.base_weight;
    if (rule.base_weight >= STRONG_MATCH_THRESHOLD) {
      existing.isStrongMatch = true;
    }
    existing.hits.push({ ruleId: rule.ruleId, contribution: rule.base_weight });
    accumulated.set(rule.capability_key, existing);
  }

  return accumulated;
}

function assembleResults(
  accumulated: Map<CapabilityKey, AccumulatedEntry>,
  includeDebug: boolean
): InferredCapabilityDebug[] {
  const results: InferredCapabilityDebug[] = [];

  for (const [key, entry] of accumulated.entries()) {
    // Filter: pass if score >= MIN_SCORE, or bypass for strong-match rules
    if (entry.rawScore < MIN_SCORE && !entry.isStrongMatch) continue;
    if (!validateCapabilityKey(key)) continue;

    results.push({
      capability_key: key,
      weight: clampWeight(entry.rawScore),
      is_primary: false,
      rawScore: entry.rawScore,
      hits: includeDebug ? entry.hits : [],
    });
  }

  // Sort descending by weight
  results.sort((a, b) => b.weight - a.weight);

  // Primary selection: prefer strong-match keys (weight >= PRIMARY_WEIGHT_THRESHOLD)
  const primaryCount = Math.min(2, results.length);
  const primarySet = new Set<CapabilityKey>();

  // First pass: strong candidates only
  for (const r of results) {
    if (primarySet.size >= primaryCount) break;
    if (r.weight >= PRIMARY_WEIGHT_THRESHOLD) primarySet.add(r.capability_key);
  }
  // Second pass: fill remainder from top of sorted list
  for (const r of results) {
    if (primarySet.size >= primaryCount) break;
    primarySet.add(r.capability_key);
  }

  results.forEach(r => { r.is_primary = primarySet.has(r.capability_key); });

  return results;
}

// =============================================
// PUBLIC API
// =============================================

/**
 * Infer capabilities from text (title + description or combined ticket signal).
 *
 * Returns capabilities with score >= 0.9 (or >= 2.0 strong-match bypass),
 * top 2 marked as is_primary (preferring weight >= 1.6).
 *
 * Backwards compatible with all existing callers.
 */
export function inferCapabilities(text: string): InferredCapability[] {
  if (!text || text.trim().length === 0) return [];
  const accumulated = runRules(text);
  return assembleResults(accumulated, false).map(({ capability_key, weight, is_primary }) => ({
    capability_key,
    weight,
    is_primary,
  }));
}

/**
 * Debug variant — returns all fields including rawScore and hits (rule IDs + contributions).
 * Used by the ?debugTraining=1 dev panel in TicketTrainingTab.
 * Not called in production paths.
 */
export function inferCapabilitiesWithDebug(text: string): InferredCapabilityDebug[] {
  if (!text || text.trim().length === 0) return [];
  const accumulated = runRules(text);
  return assembleResults(accumulated, true);
}
